<script setup lang="ts">
import { articleService } from '@/api/services/article-service'
import type { ApiCursorBody } from '@/types/api-response.types'
import type { Article } from '@/types/article.types'
import type { OpenClassFilter } from '@/types/open-class.types'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import PostListItem from './ArticleListItem.vue'

// props로 현재 선택된 필터 받기
const props = defineProps<{
  filters: OpenClassFilter
}>()

// 스크롤 감지를 위한 변수들
const isLoading = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

// 게시글 데이터
const postCursorInfo = ref<ApiCursorBody<Article>>()
const postList = ref<Article[]>([])

const ascending = computed(() => {
  return props.filters.sort.data === 'latest' ? false : true
})

// 추가 게시글 로드 함수
const loadMorePosts = async () => {
  if (isLoading.value || !postCursorInfo.value?.hasNext) return

  isLoading.value = true
  try {
    const nextCursor = postCursorInfo.value?.nextCursor
    const postCursorList = await articleService.getArticles(18, nextCursor, ascending.value)

    postList.value = [...postList.value, ...postCursorList.data]
    postCursorInfo.value = postCursorList
  } catch (error) {
    console.error('게시글 로드 중 오류 발생:', error)
  } finally {
    isLoading.value = false
  }
}

// Intersection Observer 설정
const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMorePosts()
      }
    },
    { threshold: 0.1 },
  )

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value)
  }
}

watch(props.filters, async () => {
  postList.value = []
  const postCursorList = await articleService.getArticles(18, null, ascending.value)
  postList.value = postCursorList.data
  postCursorInfo.value = postCursorList
})

onMounted(async () => {
  const postCursorList = await articleService.getArticles(18, null, ascending.value)
  postList.value = postCursorList.data
  postCursorInfo.value = postCursorList

  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<template>
  <div class="post-list">
    <PostListItem v-for="item in postList" :key="item.idx" :item="item" />

    <!-- 결과가 없는 경우 -->
    <div v-if="postList.length === 0" class="empty-state">
      <p class="body-md gray-600">해당 조건에 맞는 결과가 없습니다.</p>
    </div>

    <!-- 로딩 트리거 요소 -->
    <div ref="loadMoreTrigger" class="load-more-trigger">
      <div v-if="isLoading" class="loading-spinner">로딩 중...</div>
    </div>
  </div>
</template>

<style scoped>
.post-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 28px;
}

.load-more-trigger {
  grid-column: 1 / -1;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-spinner {
  color: var(--gray-600);
}

@media (max-width: 1365px) {
  .post-list {
    grid-template-columns: 1fr;
    gap: 28px;
    margin-top: 0px;
  }
}
</style>
